/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import React, {
    ReactNode,
    useEffect,
    useInsertionEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './ComponentIcon';
import {
    WorkspaceFocusModeToggle,
    useWorkspaceFullscreenLayout,
    useWorkspaceViewLevel,
} from './WorkspaceFocusMode';
import { compareModalTabOrder, resolveModalTabDestination } from './ModalFocus.state';
import { getModalFocusReturnTarget } from './modal-focus-return.utils';
import { useModalLabels } from './ModalLabels';
import './Modal.css';

export interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string | ReactNode;
    /** Kayıt geçmişi gibi form-bağlamlı, yetkili başlık aksiyonları. */
    headerActions?: ReactNode;
    children: ReactNode;
    /** Fixed strip between the window title and the scrolling body, for form tabs. */
    bodyHeader?: ReactNode;
    footer?: ReactNode;
    size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
    closeOnOverlayClick?: boolean;
    /** Overlay tıklamasıyla kapanmadan hemen önce çalışır. */
    onOverlayClick?: () => void;
    /** Örn. date-range popup: overlay stilini özelleştirmek için */
    overlayClassName?: string;
    /** Gerçek `.modal-content` öğesine uygulanacak ek sınıflar. */
    contentClassName?: string;
    /** Fill the available body with panels that own their internal scrolling. */
    bodyLayout?: 'scroll' | 'fill';
    /** Workspace forms remain scoped; global shell actions may explicitly cover the viewport. */
    surfaceScope?: 'workspace' | 'viewport';
    /**
     * Pencere kontrollerini kullanıcının system/windows/macos tercihine göre
     * başlığın başlangıç veya bitiş tarafına yerleştirir.
     * Varsayılan: AÇIK. Özel, pencere davranışı taşımayan yüzeyler kapatabilir.
     */
    platformAdaptiveControls?: boolean;
    /** Kapat düğmesinin erişilebilir etiketi. */
    closeAriaLabel?: string;
    /**
     * Başlıkta üç kademeli görünüm düğmesini göster:
     * normal -> odak -> tam ekran -> normal.
     * Varsayılan: AÇIK. Küçük işlem/onay modalları açıkça `false` geçebilir.
     */
    maximizable?: boolean;
    /** Opt-in child surface: normal view stays inside its opener's parent modal. */
    constrainToParent?: boolean;
    /** Normal görünümde düğmenin açıkladığı bir sonraki eylem. */
    focusAriaLabel?: string;
    /** Odak görünümünde düğmenin açıkladığı bir sonraki eylem. */
    fullscreenAriaLabel?: string;
    /** Tam ekran görünümünde düğmenin açıkladığı bir sonraki eylem. */
    restoreAriaLabel?: string;
}

type ModalOverlayStyle = React.CSSProperties & {
    '--modal-scope-left'?: string;
    '--modal-scope-right'?: string;
    '--qp-system-shell-header-size'?: string;
};

// React nesting is retained across the portal; DOM nesting must not make an
// ancestor's transform/overflow the containing block of the next window.
const ModalSurfaceContext = React.createContext<{
    layer: string;
    rootRef: React.RefObject<HTMLDivElement>;
} | null>(null);

type ModalParentStyle = React.CSSProperties & {
    '--modal-parent-left': string;
    '--modal-parent-top': string;
    '--modal-parent-width': string;
    '--modal-parent-height': string;
};

const MODAL_OVERLAY_SELECTOR = '.modal-overlay';
const MODAL_FOCUS_SCOPE_ATTRIBUTE = 'data-qubepro-modal-focus-scope';
const MODAL_FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    'iframe',
    'object',
    'embed',
    'audio[controls]',
    'video[controls]',
    '[contenteditable]:not([contenteditable="false"])',
    '[tabindex]',
].join(',');

const focusWithoutScrolling = (target: HTMLElement) => {
    try {
        target.focus({ preventScroll: true });
    } catch {
        // Eski WebView'larda FocusOptions desteklenmese de odak sözleşmesi korunur.
        target.focus();
    }
};

const isRenderedFocusTarget = (target: HTMLElement): boolean => {
    if (!target.isConnected) return false;
    if (target.matches(':disabled') || target.getAttribute('aria-disabled') === 'true') {
        return false;
    }
    if (target.closest('[hidden], [inert], [aria-hidden="true"]')) return false;

    const targetWindow = target.ownerDocument.defaultView;
    const style = targetWindow?.getComputedStyle(target);
    if (style?.display === 'none' || style?.visibility === 'hidden' || style?.visibility === 'collapse') {
        return false;
    }

    return target.getClientRects().length > 0;
};

const isProgrammaticallyFocusable = (target: HTMLElement): boolean => (
    target.matches(MODAL_FOCUSABLE_SELECTOR) && isRenderedFocusTarget(target)
);

const getModalTabbableElements = (overlay: HTMLElement): HTMLElement[] => (
    Array.from(overlay.querySelectorAll<HTMLElement>(MODAL_FOCUSABLE_SELECTOR))
        .filter((target) => target.tabIndex >= 0 && isRenderedFocusTarget(target))
        .map((target, documentIndex) => ({ target, documentIndex }))
        .sort((left, right) => compareModalTabOrder(
            { tabIndex: left.target.tabIndex, documentIndex: left.documentIndex },
            { tabIndex: right.target.tabIndex, documentIndex: right.documentIndex },
        ))
        .map(({ target }) => target)
);

const getInitialModalFocusTarget = (overlay: HTMLElement): HTMLElement => {
    const explicitTarget = [
        ...overlay.querySelectorAll<HTMLElement>('[data-autofocus]'),
        ...overlay.querySelectorAll<HTMLElement>('[autofocus]:not([data-autofocus])'),
    ].find(isProgrammaticallyFocusable);
    const reactAutoFocusTarget = overlay.ownerDocument.activeElement;
    const activeTarget = reactAutoFocusTarget instanceof HTMLElement
        && overlay.contains(reactAutoFocusTarget)
        && isProgrammaticallyFocusable(reactAutoFocusTarget)
        ? reactAutoFocusTarget
        : null;

    return explicitTarget ?? activeTarget ?? getModalTabbableElements(overlay)[0] ?? overlay;
};

const getTopmostModalOverlay = (): Element | null => {
    const overlays = Array.from(document.querySelectorAll<HTMLElement>(MODAL_OVERLAY_SELECTOR));
    return overlays.reduce<HTMLElement | null>((topmost, overlay) => {
        const layer = Number.parseInt(getComputedStyle(overlay).zIndex, 10) || 0;
        const topmostLayer = topmost ? Number.parseInt(getComputedStyle(topmost).zIndex, 10) || 0 : 0;
        return !topmost || layer >= topmostLayer ? overlay : topmost;
    }, null);
};

const isTopmostModalOverlay = (overlay: HTMLElement): boolean => (
    getTopmostModalOverlay() === overlay
);

const isRestorableFocusTarget = (target: HTMLElement | null): target is HTMLElement => (
    Boolean(target && isProgrammaticallyFocusable(target))
);

export const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    title,
    headerActions,
    children,
    bodyHeader,
    footer,
    size = 'md',
    closeOnOverlayClick = true,
    onOverlayClick,
    overlayClassName,
    contentClassName,
    bodyLayout = 'scroll',
    surfaceScope = 'workspace',
    platformAdaptiveControls = true,
    closeAriaLabel,
    maximizable,
    constrainToParent = false,
    focusAriaLabel,
    fullscreenAriaLabel,
    restoreAriaLabel,
}) => {
    const labels = useModalLabels();
    const titleId = React.useId();
    const focusScopeId = React.useId();
    const overlayRef = useRef<HTMLDivElement>(null);
    const parentSurface = React.useContext(ModalSurfaceContext);
    const rootSurfaceRef = parentSurface?.rootRef ?? overlayRef;
    const overlayPointerDownRef = useRef(false);
    const openerRef = useRef<HTMLElement | null>(null);
    const focusLifecycleRef = useRef(0);
    const viewMode = useWorkspaceViewLevel();
    const viewLevel = viewMode.level;
    const [overlayStyle, setOverlayStyle] = useState<ModalOverlayStyle>({});
    const [parentStyle, setParentStyle] = useState<ModalParentStyle | null>(null);

    const canMaximize = maximizable ?? true;
    const focused = viewLevel === 'focus';
    const fullscreen = viewLevel === 'fullscreen';
    const baseLayer = surfaceScope === 'viewport'
        ? 'var(--modal-viewport-z-index, 10000)'
        : fullscreen ? '1200' : '1000';
    const layer = parentSurface ? `max(${baseLayer}, calc(${parentSurface.layer} + 1))` : baseLayer;
    const surfaceContext = React.useMemo(() => ({ layer, rootRef: rootSurfaceRef }), [layer, rootSurfaceRef]);

    useWorkspaceFullscreenLayout({
        active: isOpen && fullscreen && !parentSurface,
        targetRef: overlayRef,
    });

    // React'in host `autoFocus` adımından önce gerçek açan kontrolü yakala.
    // Bir alt modal açılıyorsa üst modal içindeki opener geçerli hedeftir.
    useInsertionEffect(() => {
        if (!isOpen || typeof document === 'undefined') return;

        const activeElement = document.activeElement;
        if (!(activeElement instanceof HTMLElement)) return;

        const activeModal = activeElement.closest(`[${MODAL_FOCUS_SCOPE_ATTRIBUTE}]`);
        if (activeModal?.getAttribute(MODAL_FOCUS_SCOPE_ATTRIBUTE) === focusScopeId) return;
        openerRef.current = getModalFocusReturnTarget(activeElement) ?? activeElement;
    }, [focusScopeId, isOpen]);

    useLayoutEffect(() => {
        if (!isOpen) return undefined;

        const overlay = overlayRef.current;
        if (!overlay) return undefined;

        const lifecycle = ++focusLifecycleRef.current;
        const ownsFocusLifecycle = isTopmostModalOverlay(overlay);
        if (ownsFocusLifecycle) {
            focusWithoutScrolling(getInitialModalFocusTarget(overlay));
        }

        return () => {
            if (!ownsFocusLifecycle) return;

            const opener = openerRef.current;
            queueMicrotask(() => {
                // StrictMode yeniden kurulumunda veya aynı bileşen hızla yeniden
                // açıldığında eski cleanup yeni modalın odağını çalmaz.
                if (focusLifecycleRef.current !== lifecycle) return;
                openerRef.current = null;
                if (!isRestorableFocusTarget(opener)) return;

                const topmostModal = getTopmostModalOverlay();
                if (topmostModal && !topmostModal.contains(opener)) return;
                focusWithoutScrolling(opener);
            });
        };
    }, [isOpen]);

    useLayoutEffect(() => {
        if (!isOpen || !constrainToParent || viewLevel !== 'normal') {
            setParentStyle(null);
            return;
        }
        const overlay = overlayRef.current;
        const parent = openerRef.current?.closest<HTMLElement>('.modal-content');
        if (!overlay || !parent || overlay.contains(parent)) {
            setParentStyle(null);
            return;
        }
        let frame = 0;
        const updateParentBounds = () => {
            if (!parent.isConnected) return;
            const parentRect = parent.getBoundingClientRect();
            const overlayRect = overlay.getBoundingClientRect();
            const edgeGap = Number.parseFloat(getComputedStyle(overlay).getPropertyValue('--modal-edge-gap')) || 0;
            const nextStyle: ModalParentStyle = {
                '--modal-parent-left': `${parentRect.left - overlayRect.left + edgeGap}px`,
                '--modal-parent-top': `${parentRect.top - overlayRect.top + edgeGap}px`,
                '--modal-parent-width': `${Math.max(0, parentRect.width - edgeGap * 2)}px`,
                '--modal-parent-height': `${Math.max(0, parentRect.height - edgeGap * 2)}px`,
            };
            setParentStyle((current) => current
                && current['--modal-parent-left'] === nextStyle['--modal-parent-left']
                && current['--modal-parent-top'] === nextStyle['--modal-parent-top']
                && current['--modal-parent-width'] === nextStyle['--modal-parent-width']
                && current['--modal-parent-height'] === nextStyle['--modal-parent-height'] ? current : nextStyle);
        };
        const scheduleUpdate = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(updateParentBounds);
        };
        updateParentBounds();
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(scheduleUpdate);
        observer?.observe(parent);
        observer?.observe(overlay);
        window.addEventListener('resize', scheduleUpdate);
        document.addEventListener('transitionend', scheduleUpdate, true);
        document.addEventListener('scroll', scheduleUpdate, true);
        return () => {
            cancelAnimationFrame(frame);
            observer?.disconnect();
            window.removeEventListener('resize', scheduleUpdate);
            document.removeEventListener('transitionend', scheduleUpdate, true);
            document.removeEventListener('scroll', scheduleUpdate, true);
        };
    }, [constrainToParent, isOpen, viewLevel]);

    // ESC görünüm kademesini korur; Tab yalnız en üst modal içinde sarılır.
    useEffect(() => {
        const handleModalKeyboard = (e: KeyboardEvent) => {
            if (!isOpen || e.defaultPrevented || (e.key !== 'Escape' && e.key !== 'Tab')) return;

            const overlay = overlayRef.current;
            if (!overlay || !isTopmostModalOverlay(overlay)) return;

            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopImmediatePropagation();
                if (viewLevel !== 'normal') {
                    viewMode.stepBack();
                    return;
                }
                onClose();
                return;
            }

            const focusableElements = getModalTabbableElements(overlay);
            const activeIndex = focusableElements.indexOf(
                document.activeElement instanceof HTMLElement
                    ? document.activeElement
                    : overlay,
            );
            const destination = resolveModalTabDestination({
                focusableCount: focusableElements.length,
                activeIndex,
                shiftKey: e.shiftKey,
            });
            if (destination === 'native') return;

            e.preventDefault();
            e.stopImmediatePropagation();
            const focusTarget = destination === 'surface'
                ? overlay
                : destination === 'last'
                    ? focusableElements[focusableElements.length - 1]
                    : focusableElements[0];
            focusWithoutScrolling(focusTarget);
        };
        window.addEventListener('keydown', handleModalKeyboard);
        return () => window.removeEventListener('keydown', handleModalKeyboard);
    }, [isOpen, onClose, viewLevel, viewMode.stepBack]);

    // Her açılış kanonik normal boyuttan başlar.
    useEffect(() => {
        if (!isOpen) viewMode.reset();
    }, [isOpen, viewMode.reset]);

    useLayoutEffect(() => {
        if (!isOpen) {
            setOverlayStyle({});
            return undefined;
        }

        if (surfaceScope === 'viewport') {
            setOverlayStyle({ '--modal-scope-left': '0px', '--modal-scope-right': '0px' });
            return undefined;
        }
        let frame = 0;
        let observer: ResizeObserver | null = null;

        const resolveScope = () =>
            rootSurfaceRef.current?.closest<HTMLElement>('[data-qp-main-content="true"], .main-content') ??
            document.querySelector<HTMLElement>('[data-qp-main-content="true"], .main-content');

        const updateScope = () => {
            const scope = resolveScope();
            if (!scope) {
                setOverlayStyle({});
                return;
            }
            const rect = scope.getBoundingClientRect();
            const left = `${Math.max(0, Math.round(rect.left))}px`;
            const right = `${Math.max(0, Math.round(window.innerWidth - rect.right))}px`;
            const headerSize = getComputedStyle(scope).getPropertyValue('--qp-system-shell-header-size').trim() || undefined;
            setOverlayStyle((current) =>
                current['--modal-scope-left'] === left && current['--modal-scope-right'] === right
                    && current['--qp-system-shell-header-size'] === headerSize
                    ? current
                    : { '--modal-scope-left': left, '--modal-scope-right': right, '--qp-system-shell-header-size': headerSize },
            );
        };

        const scheduleUpdate = () => {
            window.cancelAnimationFrame(frame);
            frame = window.requestAnimationFrame(updateScope);
        };

        const scope = resolveScope();
        scheduleUpdate();
        window.addEventListener('resize', scheduleUpdate);
        document.addEventListener('transitionend', scheduleUpdate, true);

        if (scope && typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(scheduleUpdate);
            observer.observe(scope);
            const workspace = scope.closest<HTMLElement>('.layout-workspace');
            if (workspace) observer.observe(workspace);
        }

        return () => {
            window.cancelAnimationFrame(frame);
            window.removeEventListener('resize', scheduleUpdate);
            document.removeEventListener('transitionend', scheduleUpdate, true);
            observer?.disconnect();
        };
    }, [isOpen, surfaceScope, rootSurfaceRef]);

    if (!isOpen) return null;

    const surface = (
        <div
            ref={overlayRef}
            className={[
                'modal-overlay',
                'qubepro-modal-overlay',
                focused ? 'modal-overlay-focused' : '',
                fullscreen ? 'modal-overlay-fullscreen' : '',
                parentStyle && viewLevel === 'normal' ? 'modal-overlay-parent-constrained' : '',
                surfaceScope === 'viewport' ? 'modal-scope-viewport' : '',
                overlayClassName,
            ].filter(Boolean).join(' ')}
            style={{ ...overlayStyle, ...parentStyle, zIndex: layer }}
            onPointerDown={(event) => {
                overlayPointerDownRef.current = event.target === event.currentTarget;
            }}
            onClick={closeOnOverlayClick ? (event) => {
                const shouldClose =
                    overlayPointerDownRef.current &&
                    event.target === event.currentTarget;
                overlayPointerDownRef.current = false;
                if (shouldClose) {
                    onOverlayClick?.();
                    onClose();
                }
            } : undefined}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            tabIndex={-1}
            data-qubepro-modal-focus-scope={focusScopeId}
            data-modal-view-level={viewLevel}
        >
            <div
                className={[
                    'modal-content',
                    `modal-size-${size}`,
                    focused ? 'modal-focused modal-maximized' : '',
                    fullscreen ? 'modal-fullscreen' : '',
                    platformAdaptiveControls ? 'modal-platform-adaptive-controls' : '',
                    bodyLayout === 'fill' ? 'modal-body-fill' : '',
                    contentClassName,
                ].filter(Boolean).join(' ')}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="modal-header">
                    <div className="modal-window-controls">
                        {canMaximize && (
                            <WorkspaceFocusModeToggle
                                className="modal-header-btn"
                                mode={viewLevel}
                                enterLabel={focusAriaLabel ?? labels.modalFocus}
                                fullscreenLabel={fullscreenAriaLabel ?? labels.modalFullscreen}
                                exitLabel={restoreAriaLabel ?? labels.modalRestore}
                                onToggle={viewMode.advance}
                                data-modal-view-toggle="true"
                                data-modal-view-level={viewLevel}
                            >
                                <Icon name={fullscreen ? 'focusModeExit' : 'focusModeEnter'} size={18} />
                            </WorkspaceFocusModeToggle>
                        )}
                        <button className="modal-close modal-header-btn" onClick={onClose} aria-label={closeAriaLabel ?? labels.close}>
                            <Icon name="close" size={20} />
                        </button>
                    </div>
                    <div className="modal-title" id={titleId}>
                        {typeof title === 'string' ? <h2>{title}</h2> : title}
                    </div>
                    {headerActions ? (
                        <div className="modal-context-actions">
                            {headerActions}
                        </div>
                    ) : null}
                </div>

                {bodyHeader ? <div className="modal-body-header">{bodyHeader}</div> : null}
                <div className="modal-body">
                    {children}
                </div>

                {footer && (
                    <div className="modal-footer">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
    return (
        <ModalSurfaceContext.Provider value={surfaceContext}>
            {(parentSurface || surfaceScope === 'viewport') && typeof document !== 'undefined'
                ? createPortal(surface, document.body)
                : surface}
        </ModalSurfaceContext.Provider>
    );
};
